import { AfterViewInit, Component, TemplateRef, ViewChild, inject } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { debounceTime, map } from "rxjs/operators";
import {
  CstListViewDataSource,
  CstListViewRowStyle,
  ICstListViewColumn,
  ICstListViewState,
} from "src/app/common-components/list-view/models";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { Claims } from "src/app/constants";
import { UserService } from "src/app/services/user.service";
import {
  FacilityMonitoringClientProvider,
} from "src/app/web-api/web-api";
import { MonitoringBlackboxLookupViewModel } from "../../models/monitoring";

export interface IAnlageApiMonitoringBlackboxSelectDialogData {
  searchText?: string;
}

@Component({
    templateUrl: "./monitoring-blackbox-select.component.html",
    styleUrls: ["./monitoring-blackbox-select.component.scss"],
    standalone: false
})
export class AnlageApiMonitoringBlackboxSelectDialogComponent
  extends CstResponsivePopupComponentBase<IAnlageApiMonitoringBlackboxSelectDialogData>
  implements AfterViewInit {
  private _client = inject(FacilityMonitoringClientProvider);
  private _userService = inject(UserService);

  formFilter: FormGroup = null;

  @ViewChild("columnStatus") templateRefColumnsStatus: TemplateRef<any>;

  statusColumn: ICstListViewColumn<MonitoringBlackboxLookupViewModel> = {
    field: "isLinked",
    header: "",
    width: "70px",
    template: null,
  };
  columns: ICstListViewColumn<MonitoringBlackboxLookupViewModel>[] = [
    this.statusColumn,
    {
      field: "id",
      header: $localize`:@@AnlageAPI_MonitoringBlackboxSelect_Header_ID:ID`,
      width: "10%",
    },
    {
      field: "type",
      header: $localize`:@@AnlageAPI_MonitoringBlackboxSelect_Header_Type:Type`,
      width: "10%",
    },
    {
      field: "facilityNo",
      header: $localize`:@@AnlageAPI_MonitoringBlackboxSelect_Header_FacilityNo:Facility No.`,
      width: "20%",
    },
    {
      field: "name",
      header: $localize`:@@AnlageAPI_MonitoringBlackboxSelect_Header_Name:Name`,
      width: "20%",
    },
    {
      field: "location",
      header: $localize`:@@AnlageAPI_MonitoringBlackboxSelect_Header_Location:Location`,
      width: "40%",
    },
  ];

  dataSource = new CstListViewDataSource<MonitoringBlackboxLookupViewModel>({
    pagination: { pageSize: 10 },
    grouping: {
      columnName: "systemType",
    },
  });

  constructor() {
    const fb = inject(FormBuilder);

    super();
    this.formFilter = fb.group({
      searchText: [""],
    });
    this.formFilter.valueChanges
      .pipe(debounceTime(1000))
      .subscribe(this._OnFilterChanged.bind(this));
    this.dataSource.registerFetchDataCallback(this._LoadBlackboxes.bind(this));
  }

  onOpening() {
    return this._userService.HasClaim(Claims.IsInternal);
  }

  onInitialised() {
    if (this.data) {
      this.formFilter.patchValue(this.data);
    } else {
      this.dataSource.refresh();
    }
  }

  ngAfterViewInit(): void {
    this.statusColumn.template = this.templateRefColumnsStatus;
  }

  public canBlackboxSelect(item: MonitoringBlackboxLookupViewModel) {
    return !item.isLinked;
  }
  public onBlackboxSelect(item: MonitoringBlackboxLookupViewModel) {
    if (!this.canBlackboxSelect(item)) {
      return;
    }
    this.close(item.getModel());
  }

  public getRowStyle(
    item: MonitoringBlackboxLookupViewModel
  ): CstListViewRowStyle {
    return item.isLinked ? "disabled" : "default";
  }

  private _LoadBlackboxes(state: ICstListViewState) {
    return this._client
      .getBlackboxes(state.pagination.offset, state.pagination.pageSize)
      .pipe(
        map((result) => ({
          data: result.data.map(
            (x) => new MonitoringBlackboxLookupViewModel(x)
          ),
          total: result.total,
        }))
      );
  }

  private _OnFilterChanged() {
    this.dataSource.refresh();
  }
}
